import { STORAGE, STORAGE_KEY } from "@/constants";
import { getItem, removeItem, setItem } from "@/lib/storage-utils";

export type PermissionState =
  | "unsupported"
  | "default"
  | "granted"
  | "denied"
  | "dismissed";

const supportsNotifications = (): boolean =>
  typeof window !== "undefined" && "Notification" in window;

// iOS Safari (16.4+) exposes the Notification API only when the page is
// installed to the home screen as a PWA. Detecting this lets the UI tell users
// what to do instead of showing a useless disabled toggle.
export const isIosWithoutPwa = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const isIos = /iPhone|iPad|iPod/.test(navigator.userAgent);
  const isStandalone = Boolean(
    (navigator as Navigator & { standalone?: boolean }).standalone,
  );
  return isIos && !isStandalone;
};

export const getPermissionState = (): PermissionState => {
  if (!supportsNotifications()) return "unsupported";
  if (Notification.permission === "denied") return "denied";
  const dismissed = getItem<boolean>(
    STORAGE_KEY.NOTIFICATION_DISMISSED,
    STORAGE.LOCAL,
  );
  if (dismissed) return "dismissed";
  if (Notification.permission === "granted") return "granted";
  return "default";
};

export const requestPermission = async (): Promise<PermissionState> => {
  if (!supportsNotifications()) return "unsupported";
  removeItem(STORAGE_KEY.NOTIFICATION_DISMISSED, STORAGE.LOCAL);
  await Notification.requestPermission();
  return getPermissionState();
};

export const disable = (): void => {
  setItem(STORAGE_KEY.NOTIFICATION_DISMISSED, true, STORAGE.LOCAL);
};

export const isNotificationsActive = (): boolean =>
  getPermissionState() === "granted";
